import type { OrganizerCourseUpdates } from "../types/organizerCourse";
import type { RaceRule } from "../utils/raceRules";
import {
  collection,
  deleteDoc,
  type DocumentData,
  doc,
  documentId,
  getDoc,
  getDocs,
  getDocsFromServer,
  Timestamp,
  limit,
  onSnapshot,
  orderBy,
  query,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  setDoc,
  startAfter,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import type { LocaleCode } from "../localization";
import type { VenueAmenity } from "../types/venueAmenities";
import { MAX_INSURANCE_CERTIFICATE_BYTES, type RaceInsuranceCertificateRecord, type RaceInsuranceInput } from "../types/raceInsurance";
import {
  AidStation,
  EventEntryType,
  FeatureFlags,
  GearItem,
  KompeteLocation,
  KompeteUser,
  RegistrationRecord,
  Race,
  RaceCue,
  RaceResult,
  RaceFormat,
  RaceDifficulty,
  RaceProximitySettings,
  RaceSurfaceType,
  Checkpoint,
  RefundPolicyType,
  Sponsor,
  RaceCategory,
  RaceStartPolicy,
  RacerLocation,
  SportType,
  StripeConnectStatus,
  UserRace,
  UserGender,
  WorldTimeZone,
} from "../types/iosModels";
import {
  DiscoverContinentId,
  discoverContinentContainsCountry,
  normalizeDiscoverCountryName,
} from "../utils/discoverLocationFilters";
import {
  discoverTextMatchesSearchQuery,
  getDiscoverSearchQueryToken,
} from "../utils/discoverSearchIndex";
import { getStripeMinorUnitMultiplier } from "../utils/stripeCurrencies";
import {
  getRaceCity,
  getRaceCountry,
  getRaceDescription,
  getRaceEffectiveEndDate,
  getRaceName,
  parseFeatureFlags,
  parseRegistration,
  parseRace,
  parseRaceResult,
  parseRacerLocation,
  parseUserRace,
  parseUser,
  toDateSafe,
} from "../utils/iosModelParsers";
import { functionsService } from "./functionsService";
import { getCurrentViewerRaceAccessState } from "./testRaceAccessService";

const COLLECTIONS = {
  races: "races",
  raceResults: "raceResults",
  raceInsurance: "raceInsurance",
  users: "users",
  organizers: "organizers",
  userRaces: "userRaces",
  configurations: "configurations",
  featureFlags: "featureFlags",
  racersLocations: "racersLocations",
  registrations: "registrations",
  organizerFinance: "organizerFinance",
};

const DOCUMENTS = {
  configurationsDocumentId: "1N7ViTzLKmTy9vMcVF1r",
  featureFlagsDocumentId: "5CcqrdzosVktLsk5vmq9",
};

const asRecord = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return value as Record<string, unknown>;
};

const normalizeEmailList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .filter((entry): entry is string => typeof entry === "string")
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right));
};

const mapRaceDocument = (id: string, data: unknown): Race => {
  const record = asRecord(data);
  return parseRace({ ...record, id }, id);
};

const buildDiscoverRaceSearchText = (race: Race): string =>
  [
    getRaceName(race),
    getRaceDescription(race),
    race.event.organizer,
    getRaceCity(race),
    getRaceCountry(race),
    race.event.address,
    race.sportType,
    race.surfaceType,
    race.difficulty,
    race.format.kind,
    race.event.entryType,
    race.categories,
    race.prizes,
    race.sponsors.map((sponsor) => sponsor.name),
    race.checkpoints.map((checkpoint) => checkpoint.name),
    race.aidStations.map((station) => station.name),
    race.raceCues.flatMap((cue) => [cue.title, cue.description]),
  ]
    .flat(2)
    .filter((value): value is string => typeof value === "string")
    .join(" ");

const mapRacerLocationDocument = (
  id: string,
  data: unknown
): RacerLocation | null => {
  const record = asRecord(data);
  return parseRacerLocation({ ...record, id }, id);
};

const mapUserDocument = (id: string, data: unknown): KompeteUser => {
  const record = asRecord(data);
  return parseUser({ ...record, id }, id);
};

const mapUserRaceDocument = (id: string, data: unknown): UserRace => {
  const record = asRecord(data);
  return parseUserRace({ ...record, id }, id);
};

const mapRegistrationDocument = (
  id: string,
  data: unknown
): RegistrationRecord => {
  const record = asRecord(data);
  return parseRegistration({ ...record, id }, id);
};

const mapRaceResultDocument = (id: string, data: unknown): RaceResult => {
  const record = asRecord(data);
  return parseRaceResult({ ...record, id }, id);
};

const amountFromMinorUnits = (amount: number, currency: string): number => {
  return amount / getStripeMinorUnitMultiplier(currency);
};

const getNonNegativeInteger = (value: unknown): number => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return 0;
  }
  return Math.round(numericValue);
};

const mapOrganizerFinanceSummaryDocument = (
  data: unknown
): OrganizerFinanceSummary => {
  const record = asRecord(data);
  const currencies = asRecord(record.currencies);
  const totalsByCurrency = Object.entries(currencies)
    .filter(([currency]) => /^[A-Z]{3}$/.test(currency))
    .map(([currency, summaryValue]) => {
      const summary = asRecord(summaryValue);
      const grossSalesMinor = getNonNegativeInteger(summary.grossSalesMinor);
      const racerServiceFeesMinor = getNonNegativeInteger(
        summary.racerServiceFeesMinor
      );
      return {
        currency,
        grossSales: amountFromMinorUnits(
          grossSalesMinor,
          currency
        ),
        baseEntrySales: amountFromMinorUnits(
          getNonNegativeInteger(summary.baseEntrySalesMinor) ||
            Math.max(grossSalesMinor - racerServiceFeesMinor, 0),
          currency
        ),
        racerServiceFees: amountFromMinorUnits(
          racerServiceFeesMinor,
          currency
        ),
        totalRacerCharges: amountFromMinorUnits(
          getNonNegativeInteger(summary.totalRacerChargesMinor) || grossSalesMinor,
          currency
        ),
        taxCollected: amountFromMinorUnits(
          getNonNegativeInteger(summary.taxCollectedMinor),
          currency
        ),
        organizerTaxCollected: amountFromMinorUnits(
          getNonNegativeInteger(summary.organizerTaxCollectedMinor),
          currency
        ),
        rauxaTaxCollected: amountFromMinorUnits(
          getNonNegativeInteger(summary.rauxaTaxCollectedMinor),
          currency
        ),
        refunds: amountFromMinorUnits(
          getNonNegativeInteger(summary.totalRefundsMinor) ||
            getNonNegativeInteger(summary.refundsMinor),
          currency
        ),
        refundedBaseEntry: amountFromMinorUnits(
          getNonNegativeInteger(summary.refundedBaseEntryMinor),
          currency
        ),
        refundedRacerServiceFees: amountFromMinorUnits(
          getNonNegativeInteger(summary.refundedRacerServiceFeesMinor),
          currency
        ),
        refundedTax: amountFromMinorUnits(
          getNonNegativeInteger(summary.refundedTaxMinor),
          currency
        ),
        organizerTaxRefunded: amountFromMinorUnits(
          getNonNegativeInteger(summary.organizerTaxRefundedMinor),
          currency
        ),
        rauxaTaxRefunded: amountFromMinorUnits(
          getNonNegativeInteger(summary.rauxaTaxRefundedMinor),
          currency
        ),
        rauxaShare: amountFromMinorUnits(
          getNonNegativeInteger(summary.rauxaShareMinor),
          currency
        ),
        platformCommission: amountFromMinorUnits(
          getNonNegativeInteger(summary.platformCommissionMinor) ||
            getNonNegativeInteger(summary.rauxaShareMinor),
          currency
        ),
        rauxaGrossRetained: amountFromMinorUnits(
          getNonNegativeInteger(summary.rauxaGrossRetainedMinor) ||
            getNonNegativeInteger(summary.rauxaShareMinor),
          currency
        ),
        organizerProceeds: amountFromMinorUnits(
          getNonNegativeInteger(summary.organizerProceedsMinor),
          currency
        ),
        lostDisputes: amountFromMinorUnits(
          getNonNegativeInteger(summary.lostDisputesMinor),
          currency
        ),
        paidRegistrations: getNonNegativeInteger(summary.paidRegistrationCount),
        refundedRegistrations: getNonNegativeInteger(
          summary.refundedRegistrationCount
        ),
      };
    })
    .sort((left, right) => left.currency.localeCompare(right.currency));

  return {
    paidRegistrations: getNonNegativeInteger(record.paidRegistrations),
    pendingPayments: getNonNegativeInteger(record.pendingPayments),
    failedPayments: getNonNegativeInteger(record.failedPayments),
    refundedRegistrations: getNonNegativeInteger(record.refundedRegistrations),
    totalsByCurrency,
    updatedAt: toDateSafe(record.updatedAt),
    isBackfillComplete: record.backfillState === "complete",
  };
};

const canViewerSeeRace = (
  race: Race,
  viewerAccess: { canAccessTestRaces: boolean }
): boolean => {
  return viewerAccess.canAccessTestRaces || !race.event.isTest;
};

const filterSiteVisibleRaces = (
  races: Race[],
  viewerAccess: { canAccessTestRaces: boolean }
): Race[] => {
  return races.filter((race) => canViewerSeeRace(race, viewerAccess));
};

const filterDiscoverVisibleRaces = (
  races: Race[],
  viewerAccess: { canAccessTestRaces: boolean; isAdmin: boolean },
  includeUndiscoverable: boolean
): Race[] => {
  return races.filter((race) => {
    if (race.event.isTest) {
      return viewerAccess.canAccessTestRaces;
    }

    if (race.event.isDiscoverable) {
      return true;
    }

    return includeUndiscoverable && viewerAccess.isAdmin;
  });
};

export type CreateRaceDraftInput = {
  requireParticipantId?: boolean;
  insurance: RaceInsuranceInput;
  raceId?: string;
  organizerUserId: string;
  organizerName: string;
  organizerEmail?: string | null;
  organizerContact?: OrganizerContactInfo;
  notificationEmail: string;
  emailSubject: string;
  notifyOrganizerOnRegistration?: boolean;
  emailBody: string;
  organizerPhotoURL?: string | null;
  name: string;
  shortDescription: string;
  description: string;
  city: string;
  state?: string | null;
  province?: string | null;
  country: string;
  address: string;
  postalCode?: string | null;
  eventTimeZone: WorldTimeZone;
  sportType: SportType;
  difficulty: RaceDifficulty;
  startPolicy: RaceStartPolicy;
  startDate: Date;
  endDate: Date;
  eventStartDate?: Date;
  eventEndDate?: Date;
  effectiveOpenWindowStart?: Date | null;
  effectiveOpenWindowEnd?: Date | null;
  registrationOpenDate: Date;
  registrationCloseDate: Date;
  entryType: EventEntryType;
  price: string;
  currency: string;
  refundPolicyType?: RefundPolicyType | null;
  refundDeadline?: Date | null;
  isRefundable?: boolean;
  maxAllowedParticipants?: number | null;
  mobileCoverImageUrl?: string | null;
  webCoverImageUrl?: string | null;
  photosFirebaseURLs?: string[];
  sponsors?: Sponsor[];
  venueAmenities?: VenueAmenity[];
  format: RaceFormat;
  totalLaps?: number | null;
  gpxFileFirebaseURLString?: string | null;
  startLocation?: KompeteLocation | null;
  endLocation?: KompeteLocation | null;
  checkInLocation?: KompeteLocation | null;
  checkInStartTime?: Date | null;
  checkInCloseTime?: Date | null;
  routePolyline?: KompeteLocation[];
  checkpoints?: Checkpoint[];
  aidStations?: AidStation[];
  raceCues?: RaceCue[];
  proximitySettings: RaceProximitySettings;
  categories?: RaceCategory[];
  prizes?: string[];
  waiver?: string;
  raceMeetingTime?: Date | null;
  podiumCeremonyDate?: Date | null;
  length?: number | null;
  surfaceType?: RaceSurfaceType;
  totalAscent?: number | null;
  totalDescent?: number | null;
  targetDistance?: number | null;
  isCheckInEnabled?: boolean | null;
  isFlexibleRoute?: boolean;
  allowsReEntry?: boolean;
  minimumFinishTimeSeconds?: number;
  offlineResultSyncGraceHours?: number;
  enforceOffRoute?: boolean;
  courseVerificationEnabled?: boolean;
  showsRacersLocations?: boolean;
  shouldBypassLocationCheckIn?: boolean;
  rules?: string[];
      ruleDetails?: RaceRule[];
  isDiscoverable?: boolean;
  isTest?: boolean;
};

export type MyRacesRole = "registered" | "organizing";
export type MyRacesPageCursor = {
  userId: string;
  role: MyRacesRole;
  lastDocument: QueryDocumentSnapshot<DocumentData>;
};
export type MyRacesPage = {
  races: Race[];
  nextCursor: MyRacesPageCursor | null;
  hasMore: boolean;
};
const MY_RACES_PAGE_SIZE = 25;

export type DiscoverRacesPageCursor = QueryDocumentSnapshot<DocumentData>;

export type DiscoverRacesPage = {
  races: Race[];
  nextCursor: DiscoverRacesPageCursor | null;
  hasMore: boolean;
};

export const RACE_SUBMISSION_STATUSES = ["pending", "approved", "denied"] as const;

export type RaceSubmissionStatus = (typeof RACE_SUBMISSION_STATUSES)[number];

export type RaceSubmissionDecision = "approved" | "denied";

export type OrganizerFinanceCurrencySummary = {
  currency: string;
  grossSales: number;
  baseEntrySales?: number;
  racerServiceFees?: number;
  totalRacerCharges?: number;
  taxCollected?: number;
  organizerTaxCollected?: number;
  rauxaTaxCollected?: number;
  refunds: number;
  refundedBaseEntry?: number;
  refundedRacerServiceFees?: number;
  refundedTax?: number;
  organizerTaxRefunded?: number;
  rauxaTaxRefunded?: number;
  rauxaShare: number;
  platformCommission?: number;
  rauxaGrossRetained?: number;
  organizerProceeds: number;
  lostDisputes?: number;
  paidRegistrations: number;
  refundedRegistrations: number;
};

export type OrganizerFinanceSummary = {
  paidRegistrations: number;
  pendingPayments: number;
  failedPayments: number;
  refundedRegistrations: number;
  totalsByCurrency: OrganizerFinanceCurrencySummary[];
  updatedAt: Date | null;
  isBackfillComplete: boolean;
};

export const PAID_RACE_ACCESS_STATUSES = [
  "notApplied",
  "pending",
  "approved",
  "denied",
] as const;

export type PaidRaceAccessStatus = (typeof PAID_RACE_ACCESS_STATUSES)[number];

export type PaidRaceAccessReviewDecision = "approved" | "denied" | "reopened";

export type PaidRaceAccessReviewEvent = {
  decision: PaidRaceAccessReviewDecision;
  fromStatus: PaidRaceAccessStatus;
  toStatus: PaidRaceAccessStatus;
  reason: string | null;
  reviewedAt: Date | null;
  reviewedByUid: string | null;
};

export type PaidRaceAccess = {
  status: PaidRaceAccessStatus;
  appliedAt: Date | null;
  reviewedAt: Date | null;
  reviewedByUid: string | null;
  reviewReason: string | null;
  reviewHistory: PaidRaceAccessReviewEvent[];
};

export type PaidRaceApplication = {
  organizationName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  country: string;
  websiteUrl: string;
  socialUrl: string;
  hostedBefore: string;
  pastEventLinks: string;
  expectedRaceSize: string;
  promotionPlan: string;
  refundPolicy: string;
  notes: string;
};

export type PaidRaceApplicationInput = PaidRaceApplication;

export type PaidRaceAccessNotification = {
  to: string | string[] | null;
  subject: string | null;
  body: string | null;
  status: string | null;
  queuedAt: Date | null;
  sentAt: Date | null;
  lastAttemptAt: Date | null;
  error: string | null;
};

export type OrganizerContactInfo = {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  websiteUrl: string;
  socialUrl: string;
  organizationName: string;
};

export type OrganizerProfile = OrganizerContactInfo & {
  userId: string;
  stripeAccountId: string | null;
  stripeOnboardingComplete: boolean;
  stripeChargesEnabled: boolean;
  stripePayoutsEnabled: boolean;
  stripeDetailsSubmitted: boolean;
  stripeDisabledReason: string | null;
  paidRaceAccess: PaidRaceAccess;
  paidRaceApplication: PaidRaceApplication | null;
  paidRaceAccessRequestNotification: PaidRaceAccessNotification | null;
  paidRaceAccessDecisionNotification: PaidRaceAccessNotification | null;
  createdAt: Date | null;
  updatedAt: Date | null;
};

export type OrganizerPayoutStatus =
  | "notStarted"
  | "actionNeeded"
  | "restricted"
  | "ready";

export type AdminOrganizerRecord = OrganizerProfile & {
  numberOfRacesOrganized: number;
  totalRegisteredRacers: number;
  payoutStatus: OrganizerPayoutStatus;
  payoutStatusLabel: string;
};

export type RaceSubmissionRecord = {
  raceId: string;
  race: Race;
  status: RaceSubmissionStatus;
  submittedAt: Date | null;
  reviewedAt: Date | null;
  reviewedByUid: string | null;
  reviewNotes: string | null;
  organizerUid: string | null;
  organizerEmail: string | null;
  notificationEmail: string | null;
};

const parseRaceSubmissionStatus = (value: unknown): RaceSubmissionStatus => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "approved") {
      return "approved";
    }
    if (normalized === "denied") {
      return "denied";
    }
  }

  return "pending";
};

const mapRaceSubmissionDocument = (id: string, data: unknown): RaceSubmissionRecord => {
  const record = asRecord(data);

  return {
    raceId: id,
    race: mapRaceDocument(id, data),
    status: parseRaceSubmissionStatus(record.submissionStatus),
    submittedAt: toDateSafe(record.submittedAt),
    reviewedAt: toDateSafe(record.reviewedAt),
    reviewedByUid: typeof record.reviewedByUid === "string" ? record.reviewedByUid : null,
    reviewNotes: typeof record.reviewNotes === "string" ? record.reviewNotes : null,
    organizerUid: typeof record.organizerUid === "string" ? record.organizerUid : null,
    organizerEmail: typeof record.organizerEmail === "string" ? record.organizerEmail : null,
    notificationEmail:
      typeof record.notificationEmail === "string" ? record.notificationEmail : null,
  };
};

const getOptionalString = (record: Record<string, unknown>, key: string): string => {
  const value = record[key];
  return typeof value === "string" ? value : "";
};

const parsePaidRaceAccessStatus = (value: unknown): PaidRaceAccessStatus => {
  if (typeof value === "string") {
    const match = PAID_RACE_ACCESS_STATUSES.find((status) => status === value);
    if (match) {
      return match;
    }
  }

  return "notApplied";
};

const mapPaidRaceAccess = (value: unknown): PaidRaceAccess => {
  const record = asRecord(value);
  const reviewHistory = Array.isArray(record.reviewHistory)
    ? record.reviewHistory.reduce<PaidRaceAccessReviewEvent[]>((history, entry) => {
        const historyRecord = asRecord(entry);
        const decision =
          historyRecord.decision === "approved" ||
          historyRecord.decision === "denied" ||
          historyRecord.decision === "reopened"
            ? historyRecord.decision
            : null;
        if (!decision) {
          return history;
        }
        history.push({
          decision,
          fromStatus: parsePaidRaceAccessStatus(historyRecord.fromStatus),
          toStatus: parsePaidRaceAccessStatus(historyRecord.toStatus),
          reason:
            typeof historyRecord.reason === "string" ? historyRecord.reason : null,
          reviewedAt: toDateSafe(historyRecord.reviewedAt),
          reviewedByUid:
            typeof historyRecord.reviewedByUid === "string"
              ? historyRecord.reviewedByUid
              : null,
        });
        return history;
      }, [])
    : [];
  return {
    status: parsePaidRaceAccessStatus(record.status),
    appliedAt: toDateSafe(record.appliedAt),
    reviewedAt: toDateSafe(record.reviewedAt),
    reviewedByUid:
      typeof record.reviewedByUid === "string" ? record.reviewedByUid : null,
    reviewReason:
      typeof record.reviewReason === "string" ? record.reviewReason : null,
    reviewHistory,
  };
};

const mapPaidRaceApplication = (value: unknown): PaidRaceApplication | null => {
  const record = asRecord(value);
  if (Object.keys(record).length === 0) {
    return null;
  }

  return {
    organizationName: getOptionalString(record, "organizationName"),
    contactName: getOptionalString(record, "contactName"),
    contactEmail: getOptionalString(record, "contactEmail"),
    contactPhone: getOptionalString(record, "contactPhone"),
    country: getOptionalString(record, "country"),
    websiteUrl: getOptionalString(record, "websiteUrl"),
    socialUrl: getOptionalString(record, "socialUrl"),
    hostedBefore: getOptionalString(record, "hostedBefore"),
    pastEventLinks: getOptionalString(record, "pastEventLinks"),
    expectedRaceSize: getOptionalString(record, "expectedRaceSize"),
    promotionPlan: getOptionalString(record, "promotionPlan"),
    refundPolicy: getOptionalString(record, "refundPolicy"),
    notes: getOptionalString(record, "notes"),
  };
};

const mapPaidRaceAccessNotification = (
  value: unknown
): PaidRaceAccessNotification | null => {
  const record = asRecord(value);
  if (Object.keys(record).length === 0) {
    return null;
  }

  const toValue = record.to;
  return {
    to:
      typeof toValue === "string" || Array.isArray(toValue)
        ? (toValue as string | string[])
        : null,
    subject: typeof record.subject === "string" ? record.subject : null,
    body: typeof record.body === "string" ? record.body : null,
    status: typeof record.status === "string" ? record.status : null,
    queuedAt: toDateSafe(record.queuedAt),
    sentAt: toDateSafe(record.sentAt),
    lastAttemptAt: toDateSafe(record.lastAttemptAt),
    error: typeof record.error === "string" ? record.error : null,
  };
};

const mapOrganizerProfileDocument = (
  id: string,
  data: unknown
): OrganizerProfile => {
  const record = asRecord(data);
  const stripeAccountId =
    typeof record.stripeAccountId === "string" ? record.stripeAccountId : null;
  const stripeDisabledReason =
    typeof record.stripeDisabledReason === "string"
      ? record.stripeDisabledReason
      : null;

  return {
    userId: getOptionalString(record, "userId") || id,
    stripeAccountId,
    stripeOnboardingComplete: record.stripeOnboardingComplete === true,
    stripeChargesEnabled: record.stripeChargesEnabled === true,
    stripePayoutsEnabled: record.stripePayoutsEnabled === true,
    stripeDetailsSubmitted: record.stripeDetailsSubmitted === true,
    stripeDisabledReason,
    paidRaceAccess: mapPaidRaceAccess(record.paidRaceAccess),
    paidRaceApplication: mapPaidRaceApplication(record.paidRaceApplication),
    paidRaceAccessRequestNotification: mapPaidRaceAccessNotification(
      record.paidRaceAccessRequestNotification
    ),
    paidRaceAccessDecisionNotification: mapPaidRaceAccessNotification(
      record.paidRaceAccessDecisionNotification
    ),
    name: getOptionalString(record, "name"),
    email: getOptionalString(record, "email"),
    phone: getOptionalString(record, "phone"),
    address: getOptionalString(record, "address"),
    city: getOptionalString(record, "city"),
    country: getOptionalString(record, "country"),
    websiteUrl: getOptionalString(record, "websiteUrl"),
    socialUrl: getOptionalString(record, "socialUrl"),
    organizationName: getOptionalString(record, "organizationName"),
    createdAt: toDateSafe(record.createdAt),
    updatedAt: toDateSafe(record.updatedAt),
  };
};

const buildStripeStatusFromOrganizerProfile = (
  profile: OrganizerProfile
): StripeConnectStatus | null => {
  if (!profile.stripeAccountId) {
    return null;
  }

  return {
    accountId: profile.stripeAccountId,
    onboardingComplete: profile.stripeOnboardingComplete,
    chargesEnabled: profile.stripeChargesEnabled,
    payoutsEnabled: profile.stripePayoutsEnabled,
    detailsSubmitted: profile.stripeDetailsSubmitted,
    disabledReason: profile.stripeDisabledReason,
    lastAccountUpdateAt: profile.updatedAt,
  };
};

const getPayoutStatusSummary = (
  stripeConnect: StripeConnectStatus | null | undefined
): { payoutStatus: OrganizerPayoutStatus; payoutStatusLabel: string } => {
  if (!stripeConnect?.accountId) {
    return {
      payoutStatus: "notStarted",
      payoutStatusLabel: "Not started",
    };
  }

  if (stripeConnect.disabledReason) {
    return {
      payoutStatus: "restricted",
      payoutStatusLabel: "Restricted",
    };
  }

  if (
    stripeConnect.onboardingComplete &&
    stripeConnect.chargesEnabled &&
    stripeConnect.payoutsEnabled
  ) {
    return {
      payoutStatus: "ready",
      payoutStatusLabel: "Ready for paid race publishing",
    };
  }

  return {
    payoutStatus: "actionNeeded",
    payoutStatusLabel: "Action needed",
  };
};

export const firestoreService = {
  async fetchDiscoverRaces(options?: {
    country?: string;
    continent?: DiscoverContinentId | null;
    includePastRaces?: boolean;
    limitCount?: number;
    includeUndiscoverable?: boolean;
    searchQuery?: string;
  }): Promise<Race[]> {
    const page = await this.fetchDiscoverRacesPage({
      ...options,
      limitCount: options?.limitCount ?? 120,
    });
    return page.races;
  },

  async fetchDiscoverRacesPage(options?: {
    country?: string;
    continent?: DiscoverContinentId | null;
    includePastRaces?: boolean;
    limitCount?: number;
    includeUndiscoverable?: boolean;
    cursor?: DiscoverRacesPageCursor | null;
    searchQuery?: string;
  }): Promise<DiscoverRacesPage> {
    const viewerAccess = await getCurrentViewerRaceAccessState();
    const includePastRaces = options?.includePastRaces ?? true;
    const limitCount = options?.limitCount ?? 20;
    const countryKey = normalizeDiscoverCountryName(options?.country || "");
    const continent = options?.continent || null;
    const searchToken = getDiscoverSearchQueryToken(options?.searchQuery);
    const shouldUseDiscoverIndex = Boolean(countryKey || continent || searchToken);
    const includeUndiscoverable =
      Boolean(options?.includeUndiscoverable) &&
      (viewerAccess.isAdmin || viewerAccess.canAccessTestRaces);

    const racesRef = collection(db, COLLECTIONS.races);
    const queryConstraints: QueryConstraint[] = [];
    if (!includeUndiscoverable) {
      queryConstraints.push(where("event.isDiscoverable", "==", true));
      queryConstraints.push(where("event.isTest", "==", false));
    }
    if (shouldUseDiscoverIndex) {
      queryConstraints.push(where("discoverIndex.version", "==", 1));
    }
    if (countryKey) {
      queryConstraints.push(where("discoverIndex.countryKey", "==", countryKey));
    }
    if (continent) {
      queryConstraints.push(where("discoverIndex.continent", "==", continent));
    }
    if (searchToken) {
      queryConstraints.push(where("discoverIndex.searchTokens", "array-contains", searchToken));
    }
    if (options?.cursor) {
      queryConstraints.push(startAfter(options.cursor));
    }
    queryConstraints.push(limit(limitCount));

    const raceSnapshot = await getDocs(query(racesRef, ...queryConstraints));
    const raceDocs = raceSnapshot.docs;
    const nextCursor = raceDocs.length > 0 ? raceDocs[raceDocs.length - 1] : null;
    const hasMore = raceDocs.length === limitCount;

    const racesById = new Map<string, Race>();
    raceDocs.forEach((snapshotDoc) => {
      racesById.set(snapshotDoc.id, mapRaceDocument(snapshotDoc.id, snapshotDoc.data()));
    });
    const races = filterDiscoverVisibleRaces(
      Array.from(racesById.values()),
      viewerAccess,
      includeUndiscoverable
    );

    const countryFiltered = countryKey
      ? races.filter((race) =>
          normalizeDiscoverCountryName(getRaceCountry(race)) === countryKey
        )
      : races;
    const continentFiltered = continent
      ? countryFiltered.filter((race) =>
          discoverContinentContainsCountry(continent, getRaceCountry(race))
        )
      : countryFiltered;
    const discoverableRaces = searchToken
      ? continentFiltered.filter((race) =>
          discoverTextMatchesSearchQuery(
            buildDiscoverRaceSearchText(race),
            options?.searchQuery
          )
        )
      : continentFiltered;

    if (includePastRaces) {
      return { races: discoverableRaces, nextCursor, hasMore };
    }

    const now = new Date();
    const nowMs = now.getTime();
    return {
      races: discoverableRaces.filter((race) => {
        const raceEndDate = getRaceEffectiveEndDate(race);
        if (!raceEndDate) {
          return true;
        }

        return raceEndDate.getTime() >= nowMs;
      }),
      nextCursor,
      hasMore,
    };
  },

  async fetchRaceById(raceId: string): Promise<Race | null> {
    const viewerAccess = await getCurrentViewerRaceAccessState();
    const raceRef = doc(db, COLLECTIONS.races, raceId);
    const snapshot = await getDoc(raceRef).catch((error: unknown) => {
      if (asRecord(error).code === "permission-denied") return null;
      throw error;
    });

    if (!snapshot?.exists()) {
      return null;
    }

    const race = mapRaceDocument(snapshot.id, snapshot.data());
    return canViewerSeeRace(race, viewerAccess) ? race : null;
  },

  subscribeRaceById(
    raceId: string,
    handlers: {
      onData: (race: Race | null) => void;
      onError?: (error: Error) => void;
    }
  ): () => void {
    const raceRef = doc(db, COLLECTIONS.races, raceId);
    let isActive = true;
    let unsubscribeSnapshot: (() => void) | null = null;

    void getCurrentViewerRaceAccessState()
      .then((viewerAccess) => {
        if (!isActive) {
          return;
        }

        unsubscribeSnapshot = onSnapshot(
          raceRef,
          (snapshot) => {
            const nextRace = snapshot.exists()
              ? mapRaceDocument(snapshot.id, snapshot.data())
              : null;
            handlers.onData(
              nextRace && canViewerSeeRace(nextRace, viewerAccess) ? nextRace : null
            );
          },
          (error) => {
            if (error.code === "permission-denied") {
              handlers.onData(null);
            } else {
              handlers.onError?.(error);
            }
          }
        );
      })
      .catch((error: unknown) => {
        if (!isActive) {
          return;
        }
        handlers.onError?.(
          error instanceof Error ? error : new Error("Unable to verify race access.")
        );
      });

    return () => {
      isActive = false;
      unsubscribeSnapshot?.();
    };
  },

  async fetchMyRacesPage({ userId, role, cursor = null }: {
    userId: string;
    role: MyRacesRole;
    cursor?: MyRacesPageCursor | null;
  }): Promise<MyRacesPage> {
    if (!userId || (cursor && (cursor.userId !== userId || cursor.role !== role))) {
      throw new Error("Invalid My Races page scope.");
    }
    const viewerAccess = await getCurrentViewerRaceAccessState();
    const constraints: QueryConstraint[] = [role === "registered"
      ? where("registeredUserIds", "array-contains", userId)
      : where("organizerUid", "==", userId)];
    if (!viewerAccess.canAccessTestRaces) {
      constraints.push(where("event.isTest", "==", false));
    }
    // A stable ID order includes legacy races missing dates. Timeline grouping
    // and date sorting apply to the loaded rows, not to the database cursor.
    constraints.push(orderBy(documentId()));
    if (cursor) constraints.push(startAfter(cursor.lastDocument));
    const snapshot = await getDocs(query(collection(db, COLLECTIONS.races), ...constraints, limit(MY_RACES_PAGE_SIZE)));
    const lastDocument = snapshot.docs[snapshot.docs.length - 1];
    const hasMore = snapshot.docs.length === MY_RACES_PAGE_SIZE;
    return {
      races: filterSiteVisibleRaces(snapshot.docs.map((document) => mapRaceDocument(document.id, document.data())), viewerAccess),
      // Advance by the raw query page even when visibility filtering removes rows.
      nextCursor: hasMore && lastDocument ? { userId, role, lastDocument } : null,
      hasMore,
    };
  },

  async fetchRaceSubmissionById(raceId: string): Promise<RaceSubmissionRecord | null> {
    const raceRef = doc(db, COLLECTIONS.races, raceId);
    const snapshot = await getDoc(raceRef);

    if (!snapshot.exists()) {
      return null;
    }

    return mapRaceSubmissionDocument(snapshot.id, snapshot.data());
  },

  async fetchRaceInsuranceCertificate(raceId: string): Promise<RaceInsuranceCertificateRecord | null> {
    const snapshot = await getDoc(doc(db, COLLECTIONS.raceInsurance, raceId));
    if (!snapshot.exists()) {
      return null;
    }

    const record = asRecord(snapshot.data());
    const organizerUid = typeof record.organizerUid === "string" ? record.organizerUid : "";
    if (record.raceId !== raceId || !organizerUid) {
      throw new Error("Unable to load insurance details. Please try again.");
    }
    if (record.certificate === null || record.certificate === undefined) {
      return { raceId, organizerUid, certificate: null };
    }

    const certificate = asRecord(record.certificate);
    if (
      typeof certificate.storagePath !== "string" || !certificate.storagePath ||
      typeof certificate.fileName !== "string" || !certificate.fileName ||
      typeof certificate.contentType !== "string" ||
      !["application/pdf", "image/jpeg", "image/png"].includes(certificate.contentType) ||
      typeof certificate.sizeBytes !== "number" ||
      !Number.isFinite(certificate.sizeBytes) || certificate.sizeBytes <= 0 ||
      certificate.sizeBytes > MAX_INSURANCE_CERTIFICATE_BYTES
    ) {
      throw new Error("Unable to load insurance details. Please try again.");
    }

    return {
      raceId,
      organizerUid,
      certificate: {
        storagePath: certificate.storagePath,
        fileName: certificate.fileName,
        contentType: certificate.contentType,
        sizeBytes: certificate.sizeBytes,
      },
    };
  },

  async fetchRaceSubmissions(options?: {
    status?: RaceSubmissionStatus | "all";
    limitCount?: number;
  }): Promise<RaceSubmissionRecord[]> {
    const statusFilter = options?.status ?? "all";
    const limitCount = options?.limitCount ?? 200;

    const racesRef = collection(db, COLLECTIONS.races);
    const constraints: QueryConstraint[] = [];
    if (statusFilter !== "all") {
      constraints.push(where("submissionStatus", "==", statusFilter));
    }
    constraints.push(orderBy("submittedAt", "desc"), limit(limitCount));
    const snapshot = await getDocs(query(racesRef, ...constraints));

    const submissions = snapshot.docs.map((snapshotDoc) =>
      mapRaceSubmissionDocument(snapshotDoc.id, snapshotDoc.data())
    );

    return submissions.sort((left, right) => {
      const leftValue = left.submittedAt?.getTime() ?? 0;
      const rightValue = right.submittedAt?.getTime() ?? 0;
      return rightValue - leftValue;
    });
  },

  async fetchAdminOrganizers(options?: {
    limitCount?: number;
    raceLimitCount?: number;
  }): Promise<AdminOrganizerRecord[]> {
    const limitCount = options?.limitCount ?? 250;
    const raceLimitCount = options?.raceLimitCount ?? 1000;
    const organizersRef = collection(db, COLLECTIONS.organizers);
    const racesRef = collection(db, COLLECTIONS.races);

    const [organizersSnapshot, racesSnapshot] = await Promise.all([
      getDocs(query(organizersRef, limit(limitCount))),
      getDocs(query(racesRef, limit(raceLimitCount))),
    ]);

    const statsByOrganizerId = new Map<
      string,
      { numberOfRacesOrganized: number; totalRegisteredRacers: number }
    >();

    racesSnapshot.docs.forEach((snapshotDoc) => {
      const record = asRecord(snapshotDoc.data());
      const organizerId =
        getOptionalString(record, "organizerId") ||
        getOptionalString(record, "organizerUid") ||
        getOptionalString(record, "createdByUserId");

      if (!organizerId) {
        return;
      }

      const registeredUserIds = Array.isArray(record.registeredUserIds)
        ? record.registeredUserIds
        : [];
      const stats = statsByOrganizerId.get(organizerId) || {
        numberOfRacesOrganized: 0,
        totalRegisteredRacers: 0,
      };
      stats.numberOfRacesOrganized += 1;
      stats.totalRegisteredRacers += registeredUserIds.filter(
        (entry): entry is string => typeof entry === "string" && entry.trim().length > 0
      ).length;
      statsByOrganizerId.set(organizerId, stats);
    });

    return organizersSnapshot.docs
      .map((snapshotDoc) => {
        const profile = mapOrganizerProfileDocument(
          snapshotDoc.id,
          snapshotDoc.data()
        );
        const stats = statsByOrganizerId.get(profile.userId) || {
          numberOfRacesOrganized: 0,
          totalRegisteredRacers: 0,
        };
        const statusSummary = getPayoutStatusSummary(
          buildStripeStatusFromOrganizerProfile(profile)
        );

        return {
          ...profile,
          ...stats,
          payoutStatus: statusSummary.payoutStatus,
          payoutStatusLabel: statusSummary.payoutStatusLabel,
        };
      })
      .sort((left, right) => {
        const leftValue = left.updatedAt?.getTime() ?? left.createdAt?.getTime() ?? 0;
        const rightValue = right.updatedAt?.getTime() ?? right.createdAt?.getTime() ?? 0;
        return rightValue - leftValue;
      });
  },

  async fetchUserProfile(userId: string): Promise<KompeteUser | null> {
    const userRef = doc(db, COLLECTIONS.users, userId);
    const snapshot = await getDoc(userRef);

    if (!snapshot.exists()) {
      return null;
    }

    const profile = mapUserDocument(snapshot.id, snapshot.data());
    if (auth.currentUser?.uid === userId && auth.currentUser.emailVerified &&
        profile.email !== auth.currentUser.email) {
      const { email } = await functionsService.syncVerifiedAccountEmail();
      if (auth.currentUser?.uid !== userId) throw new Error("Authentication identity changed.");
      return { ...profile, email };
    }
    return profile;
  },

  async fetchOrganizerProfile(userId: string): Promise<OrganizerProfile | null> {
    const organizerRef = doc(db, COLLECTIONS.organizers, userId);
    const snapshot = await getDoc(organizerRef);

    if (!snapshot.exists()) {
      return null;
    }

    return mapOrganizerProfileDocument(snapshot.id, snapshot.data());
  },

  async fetchRegistrationById(
    registrationId: string
  ): Promise<RegistrationRecord | null> {
    const registrationRef = doc(db, COLLECTIONS.registrations, registrationId);
    const snapshot = await getDoc(registrationRef);

    if (!snapshot.exists()) {
      return null;
    }

    return mapRegistrationDocument(snapshot.id, snapshot.data());
  },

  subscribeOrganizerFinanceSummary(
    organizerUserId: string,
    handlers: {
      onData: (summary: OrganizerFinanceSummary | null) => void;
      onError?: (error: Error) => void;
    }
  ): () => void {
    const summaryRef = doc(
      db,
      COLLECTIONS.organizerFinance,
      organizerUserId
    );
    return onSnapshot(
      summaryRef,
      (snapshot) => {
        handlers.onData(
          snapshot.exists()
            ? mapOrganizerFinanceSummaryDocument(snapshot.data())
            : null
        );
      },
      (error) => {
        handlers.onError?.(error);
      }
    );
  },

  async upsertUserProfile(input: {
    userId: string;
    userProfile: {
      userName?: string | null;
      email?: string | null;
      bio?: string | null;
      city?: string | null;
      state?: string | null;
      country?: string | null;
      legalAddress?: string | null;
      profilePhotoURLString?: string | null;
      gear?: GearItem[] | null;
    };
  }): Promise<void> {
    const userRef = doc(db, COLLECTIONS.users, input.userId);
    const profile = input.userProfile;

    const payload: Record<string, unknown> = {
      updatedAt: new Date(),
    };

    if (profile.userName !== undefined) {
      payload.userName = profile.userName ? profile.userName.trim() : null;
    }
    if (profile.email !== undefined) {
      payload.email = profile.email ? profile.email.trim() : null;
    }
    if (profile.bio !== undefined) {
      payload.bio = profile.bio ? profile.bio.trim() : null;
    }
    if (profile.city !== undefined) {
      payload.city = profile.city ? profile.city.trim() : null;
    }
    if (profile.state !== undefined) {
      payload.state = profile.state ? profile.state.trim() : null;
    }
    if (profile.country !== undefined) {
      payload.country = profile.country ? profile.country.trim() : null;
    }
    if (profile.legalAddress !== undefined) {
      payload.legalAddress = profile.legalAddress ? profile.legalAddress.trim() : null;
    }
    if (profile.profilePhotoURLString !== undefined) {
      payload.profilePhotoURLString = profile.profilePhotoURLString
        ? profile.profilePhotoURLString.trim()
        : null;
    }
    if (profile.gear !== undefined) {
      payload.gear = (profile.gear || []).map((gearItem) => ({
        id: gearItem.id,
        type: gearItem.type,
        model: gearItem.model?.trim() || "",
        totalTimeUsed: gearItem.totalTimeUsed || 0,
        totalDistanceUsed: gearItem.totalDistanceUsed || 0,
      }));
    }

    await setDoc(userRef, payload, { merge: true });
  },

  async acceptOrganizerPayoutTerms(userId: string): Promise<void> {
    void userId;
    await functionsService.acceptOrganizerPayoutTerms();
  },

  async submitPaidRaceAccessApplication(
    input: PaidRaceApplicationInput
  ): Promise<{ status: PaidRaceAccessStatus }> {
    return functionsService.submitPaidRaceAccessApplication(input);
  },

  async reviewPaidRaceAccessApplication(input: {
    organizerUserId: string;
    decision: PaidRaceAccessReviewDecision;
    reviewReason?: string;
  }): Promise<{ ok: boolean; organizerUserId: string; status: PaidRaceAccessStatus }> {
    return functionsService.reviewPaidRaceAccessApplication(input);
  },

  async deleteUserProfile(userId: string): Promise<void> {
    // userRaces are iOS-owned race artifacts. The web app should not mutate them directly.
    const userRef = doc(db, COLLECTIONS.users, userId);
    await deleteDoc(userRef);
  },

  async reviewRaceSubmission(input: {
    raceId: string;
    decision: RaceSubmissionDecision;
    reviewedByUid: string;
    reviewNotes?: string;
  }): Promise<RaceSubmissionRecord> {
    await functionsService.reviewRaceSubmission({
      raceId: input.raceId,
      decision: input.decision,
      reviewNotes: input.reviewNotes,
    });

    const refreshedSnapshot = await getDoc(doc(db, COLLECTIONS.races, input.raceId));
    if (!refreshedSnapshot.exists()) {
      throw new Error("Race submission was updated but could not be reloaded.");
    }

    return mapRaceSubmissionDocument(refreshedSnapshot.id, refreshedSnapshot.data());
  },

  async fetchUserRace(input: {
    userId: string;
    userRaceId: string;
  }): Promise<UserRace | null> {
    const userRaceRef = doc(
      db,
      COLLECTIONS.users,
      input.userId,
      COLLECTIONS.userRaces,
      input.userRaceId
    );
    const snapshot = await getDoc(userRaceRef).catch((error: unknown) => {
      if (asRecord(error).code === "permission-denied") return null;
      throw error;
    });

    if (!snapshot?.exists()) {
      return null;
    }

    return mapUserRaceDocument(snapshot.id, snapshot.data());
  },

  async fetchUserRaces(userId: string, raceId?: string): Promise<UserRace[]> {
    if (!raceId) {
      // Resolve accessible IDs against current parent races on the server, then
      // read the owner's full attempts under the same database privacy rules.
      const { races } = await functionsService.getPublicRacerRaces({ userId });
      const ids = Array.from(new Set(races.map((race) => race.id)));
      const attempts: UserRace[] = [];
      for (let offset = 0; offset < ids.length; offset += 25) {
        const page = await Promise.all(ids.slice(offset, offset + 25).map((userRaceId) =>
          this.fetchUserRace({ userId, userRaceId })
        ));
        attempts.push(...page.filter((race): race is UserRace => race !== null));
      }
      return attempts;
    }
    const reference = collection(db, COLLECTIONS.users, userId, COLLECTIONS.userRaces);
    const snapshot = await getDocs(query(reference, where("raceId", "==", raceId)));
    return snapshot.docs.map(document => mapUserRaceDocument(document.id, document.data()));
  },

  async fetchNextUserRaceAttemptNumber(input: {
    userId: string;
    raceId: string;
    startPolicy: RaceStartPolicy;
  }): Promise<number | null> {
    if (input.startPolicy !== "userInitiated") {
      return null;
    }

    const attemptsSnapshot = await getDocs(
      query(
        collection(db, COLLECTIONS.users, input.userId, COLLECTIONS.userRaces),
        where("raceId", "==", input.raceId),
        limit(200)
      )
    );

    const maxAttemptNumber =
      attemptsSnapshot.docs
        .map((snapshotDoc) => {
          const value = asRecord(snapshotDoc.data()).attemptNumber;
          return typeof value === "number" ? value : 0;
        })
        .reduce((max, current) => Math.max(max, current), 0) || 0;

    return maxAttemptNumber + 1;
  },

  async fetchRaceResult(input: {
    raceId: string;
    userId: string;
  }): Promise<RaceResult | null> {
    const race = await this.fetchRaceById(input.raceId);
    if (!race) {
      return null;
    }

    const resultRef = doc(
      db,
      COLLECTIONS.races,
      input.raceId,
      COLLECTIONS.raceResults,
      input.userId
    );
    const snapshot = await getDoc(resultRef);

    if (!snapshot.exists()) {
      return null;
    }

    return mapRaceResultDocument(snapshot.id, snapshot.data());
  },

  async registerCurrentUserToRace(input: {
    raceId: string;
    userId: string;
  }): Promise<void> {
    void input;
    throw new Error(
      "Direct race registration writes are no longer supported. Use completeRaceRegistration instead."
    );
  },

  async completeRaceRegistration(input: {
    raceId: string;
    participantId?: string;
    userProfile: {
      legalFirstName: string;
      legalLastName: string;
      email: string;
      legalAddress: string;
      city: string;
      state: string;
      country?: string;
      locale: LocaleCode;
      birthDate: Date;
      gender: UserGender;
    };
    userRace: UserRace;
  }): Promise<{ userRaceId: string; registrationId: string }> {
    return functionsService.completeFreeRaceRegistration({
      raceId: input.raceId,
      participantId: input.participantId,
      userProfile: input.userProfile,
      userRace: input.userRace,
    });
  },

  async createRace(input: CreateRaceDraftInput): Promise<{ raceId: string }> {
    return functionsService.createRaceSubmission(input);
  },

  async updateOrganizerRace(input: {
    raceId: string;
    updates: OrganizerCourseUpdates & {
      name?: string;
      shortDescription?: string;
      description?: string;
      notificationEmail?: string;
      emailSubject?: string;
      notifyOrganizerOnRegistration?: boolean;
      emailBody?: string;
      rules?: string[];
      ruleDetails?: RaceRule[];
      isDiscoverable?: boolean;
      startDate?: string;
      endDate?: string;
      effectiveOpenWindowStart?: string;
      effectiveOpenWindowEnd?: string;
      eventStartDate?: string;
      eventEndDate?: string;
      mobileCoverImageUrl?: string;
      webCoverImageUrl?: string;
      photosFirebaseURLs?: string[];
      sponsors?: Sponsor[];
      venueAmenities?: VenueAmenity[];
      prizes?: string[];
      minimumFinishTimeSeconds?: number;
      offlineResultSyncGraceHours?: number;
    };
  }): Promise<void> {
    await functionsService.updateOrganizerRace(input);
  },

  async cancelOrganizerRace(raceId: string): Promise<void> {
    await functionsService.cancelOrganizerRace({ raceId });
  },

  /** A closed start window does not end an already accepted open-race attempt. */
  async hasActiveRacers(race: Race): Promise<boolean> {
    const constraints: QueryConstraint[] = [where("status", "in", ["In Race", "Paused"])];
    // Older generation-zero publishers did not include the generation field.
    if ((race.testResetGeneration ?? 0) > 0) {
      constraints.push(where("testResetGeneration", "==", race.testResetGeneration));
    }
    constraints.push(limit(1));
    const snapshot = await getDocs(query(
      collection(db, COLLECTIONS.races, race.id, COLLECTIONS.racersLocations),
      ...constraints
    ));
    return !snapshot.empty;
  },

  async fetchRacerLocationPage(input: {
    raceId: string;
    since: Timestamp | null;
    cursor: QueryDocumentSnapshot<DocumentData> | null;
    through?: Timestamp | null;
  }) {
    const reference = collection(db, COLLECTIONS.races, input.raceId, COLLECTIONS.racersLocations);
    let through = input.through ?? null;
    if (input.since && !through) {
      // Rules require serverTimestamp() on every client location write, and
      // backend projections stamp publication time. Bound this poll with that
      // trusted timestamp. Without an upper bound,
      // constantly moving racers can move beyond each cursor indefinitely.
      const latest = await getDocsFromServer(query(reference, orderBy("lastUpdated", "desc"), limit(1)));
      const timestamp = latest.docs[0]?.data().lastUpdated;
      through = timestamp instanceof Timestamp ? timestamp : input.since;
      if (through.valueOf() <= input.since.valueOf()) {
        return { changes: [], watermark: input.since, cursor: null, through };
      }
    }
    const constraints: QueryConstraint[] = [];
    if (input.since) {
      constraints.push(where("lastUpdated", ">", input.since), orderBy("lastUpdated"));
      if (through) constraints.push(where("lastUpdated", "<=", through));
    }
    constraints.push(orderBy(documentId()));
    if (input.cursor) constraints.push(startAfter(input.cursor));
    const snapshot = await getDocsFromServer(query(reference, ...constraints, limit(250)));
    let watermark = input.since ?? Timestamp.fromMillis(0);
    const changes = snapshot.docs.map(document => {
      const data = document.data();
      const timestamp = data.lastUpdated;
      if (timestamp instanceof Timestamp && timestamp.valueOf() > watermark.valueOf()) watermark = timestamp;
      return { id: document.id, racer: mapRacerLocationDocument(document.id, data) };
    });
    return { changes, watermark, through, cursor: snapshot.size === 250 ? snapshot.docs.at(-1) || null : null };
  },

  async fetchAdminConfiguration(): Promise<{
    purchasesApiKey: string | null;
    featureFlags: FeatureFlags;
    testRaceAccessEmails: string[];
  }> {
    const configRef = doc(
      db,
      COLLECTIONS.configurations,
      DOCUMENTS.configurationsDocumentId
    );
    const featureFlagsRef = doc(
      db,
      COLLECTIONS.featureFlags,
      DOCUMENTS.featureFlagsDocumentId
    );

    const [configSnapshot, featureFlagsSnapshot] = await Promise.all([
      getDoc(configRef),
      getDoc(featureFlagsRef),
    ]);

    const purchasesApiKey =
      (configSnapshot.data()?.purchasesAPIKey as string | undefined) || null;
    const featureFlags = parseFeatureFlags(featureFlagsSnapshot.data());
    const testRaceAccessEmails = normalizeEmailList(
      configSnapshot.data()?.testRaceAccessEmails
    );

    return { purchasesApiKey, featureFlags, testRaceAccessEmails };
  },
};
